import { injectCheckmark } from './form-checkboxes';

declare global {
  interface Window {
    cookieTractor: {
      consent: {
        contains: (category: string) => boolean;
        add: (category: string) => void;
      };
    };

    turnstile: {
      render: (
        container: HTMLElement,
        options: {
          sitekey: string;
          appearance: string;
          callback: () => void;
        }
      ) => void;
    };

    _turnstileLoading?: boolean;
  }
}

function hasConsent(consents?: string[]): boolean {
  if(consents)
    return consents.indexOf('functional') !== -1;

  return (
    typeof window.cookieTractor !== 'undefined' &&
    !!window.cookieTractor.consent &&
    window.cookieTractor.consent.contains('functional')
  );
}

function loadTurnstileScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if(typeof window.turnstile !== 'undefined') {
      resolve();
      return;
    }

    if(window._turnstileLoading) {
      window.addEventListener('turnstile:ready', () => resolve(), { once: true });
      window.addEventListener('turnstile:error', () => reject(new Error('Failed to load Turnstile')), { once: true });
      return;
    }

    window._turnstileLoading = true;

    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.onload = (): void => {
      window.dispatchEvent(new Event('turnstile:ready'));
      resolve();
    };

    script.onerror = (): void => {
      window._turnstileLoading = false;
      window.dispatchEvent(new Event('turnstile:error'));
      reject(new Error('Failed to load Turnstile'));
    };

    document.head.appendChild(script);
  });
}

function initTurnstile(wrapper: HTMLElement): void {
  const notice = wrapper.querySelector<HTMLElement>('.turnstile-consent-notice');
  const checkboxContainer = wrapper.querySelector<HTMLElement>('.turnstile-consent-checkbox');
  const container = wrapper.querySelector<HTMLElement>('.cf-turnstile-container');
  const checkbox = wrapper.querySelector<HTMLInputElement>('input[type="checkbox"]');
  const loader = wrapper.parentElement?.querySelector<HTMLElement>('.turnstile-loader') ?? null;
  const error = wrapper.querySelector<HTMLElement>('.turnstile-error');
  const sitekey = wrapper.dataset.sitekey ?? '';
  const form = wrapper.closest('form');
  const submitButton = form?.querySelector<HTMLInputElement>('input[type="submit"]');

  let rendered = false;

  if(!notice || !checkboxContainer || !container || !checkbox) return;

  if(!sitekey) {
    if(submitButton) submitButton.disabled = true;
    if(error) error.hidden = false;
    return;
  }

  injectCheckmark(checkboxContainer);

  async function doRender(hideNotice: boolean): Promise<void> {
    if(hideNotice) notice!.hidden = true;

    if(submitButton) submitButton.disabled = true;
    if(loader) loader.classList.add('is-visible');

    try {
      await loadTurnstileScript();
    } catch{
      if(loader) loader.classList.remove('is-visible');
      if(error) error.hidden = false;
      return;
    }

    container!.hidden = false;

    window.turnstile.render(container!, {
      sitekey,
      appearance: 'interaction-only',
      callback: (): void => {
        if(submitButton) submitButton.disabled = false;
        if(loader) loader.classList.remove('is-visible');
      }
    });
  }

  async function renderIfNotRendered(hideNotice: boolean): Promise<void> {
    if(rendered) return;

    rendered = true;
    await doRender(hideNotice);
  }

  checkbox.addEventListener('change', () => {
    if(checkbox.checked) {
      rendered = true;
      if(typeof window.cookieTractor !== 'undefined' && !!window.cookieTractor.consent)
        window.cookieTractor.consent.add('functional');
      void doRender(false);
    }
  });

  document.addEventListener('cookieTractorConsent', (e: Event) => {
    const consents = (e as CustomEvent<{ consents: string[] }>).detail?.consents ?? [];

    if(hasConsent(consents)) void renderIfNotRendered(true);
    else notice!.hidden = false;
  });

  window.addEventListener('CookieConsent', (e: Event) => {
    const current = (e as CustomEvent<{ current: string[] }>).detail?.current ?? [];

    if(hasConsent(current)) void renderIfNotRendered(true);
    else notice!.hidden = false;
  });

  if(hasConsent()) void renderIfNotRendered(true);
  else notice!.hidden = false;
}

export default function initFormTurnstile(): void {
  document.querySelectorAll<HTMLElement>('.turnstile-consent-wrapper').forEach(initTurnstile);
}
